import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Banner, Breadcrumbs, Loader, Text, useKumoToastManager } from '@cloudflare/kumo'
import { HouseIcon } from '@phosphor-icons/react'
import type { PrintJob } from '@hub-wifi/shared'
import { apiRequest } from '../api/client'
import {
  cancelPrintJob,
  listBuildingPrinters,
  listMyPrintJobs,
  uploadPrintJob,
  type BuildingPrinter,
} from '../api/printJobs'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { PrintUploadCard } from '../components/print/PrintUploadCard'
import { PrintSteps } from '../components/print/PrintSteps'
import { PrintHelp } from '../components/print/PrintHelp'
import { PrintJobRow } from '../components/print/PrintJobRow'
import { PrinterOfflineBanner } from '../components/print/PrinterOfflineBanner'
import { allPrintersOffline } from '../lib/printerConnection'
import { describePrintError } from '../lib/printErrors'

function formatExpiresIn(expiresAt: string, now: number): string {
  const ms = new Date(expiresAt).getTime() - now
  if (ms <= 0) return '0h'
  const hours = Math.floor(ms / 3_600_000)
  if (hours >= 1) return `${hours}h`
  const minutes = Math.floor(ms / 60_000)
  return `${minutes}m`
}

export function PrintCenterPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const [jobs, setJobs] = useState<PrintJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<PrintJob | null>(null)
  // Re-render every 30s so the "expires in Xh" countdown stays current
  // without storing 100 setTimeout handles.
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const i = setInterval(() => setTick((n) => n + 1), 30_000)
    return () => clearInterval(i)
  }, [])

  // Only the first load shows the full-page loader; refreshes after an
  // upload or cancel keep the page mounted (open FAQ, scroll position).
  async function load() {
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const items = await listMyPrintJobs(token)
      setJobs(items)
    } catch (err) {
      setError(describePrintError(err, t))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Best-effort: the building's printers, to warn before uploading when none
  // can print right now. Any failure just means no warning.
  const [printers, setPrinters] = useState<BuildingPrinter[]>([])
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const { building } = await apiRequest<{ building: { id: string } | null }>(
          '/api/my/building',
          { token },
        )
        if (!building) return
        const list = await listBuildingPrinters({ token, buildingId: building.id })
        if (!cancelled) setPrinters(list)
      } catch {
        // No warning.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently])
  const offlinePrinter = allPrintersOffline(printers)

  async function handleFiles(files: File[]) {
    setBusy(true)
    try {
      const token = await getAccessTokenSilently()
      for (const file of files) {
        await uploadPrintJob({ token, file })
      }
      toasts.add({ variant: 'success', title: t('print.upload.success') })
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describePrintError(err, t),
      })
    } finally {
      setBusy(false)
    }
  }

  async function confirmCancel() {
    if (!cancelTarget) return
    setBusy(true)
    try {
      const token = await getAccessTokenSilently()
      await cancelPrintJob({ token, jobId: cancelTarget.id })
      toasts.add({ variant: 'success', title: t('print.row.cancelSuccess') })
      setCancelTarget(null)
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describePrintError(err, t),
      })
    } finally {
      setBusy(false)
    }
  }

  // `tick` is read here purely so the dependency-eslint rule sees it consumed
  // — the value itself is unused; the act of incrementing the state every 30s
  // is what triggers the re-render that re-evaluates `formatExpiresIn`.
  void tick
  const now = Date.now()

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('print.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('print.title')}
        description={t('print.pageDescription')}
      />

      {error && (
        <Banner variant="error" className="mb-3">
          {error}
        </Banner>
      )}

      {offlinePrinter && (
        <PrinterOfflineBanner
          epsonConnected={offlinePrinter.epsonConnected}
          connectionCheckedAt={offlinePrinter.connectionCheckedAt}
          lastSeenConnectedAt={offlinePrinter.lastSeenConnectedAt}
        />
      )}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {jobs.length > 0 && (
            <section className="flex flex-col gap-3">
              <Text as="h2" variant="heading3">
                {t('print.queue.title', { count: jobs.length })}
              </Text>
              <div data-testid="print-job-list" className="flex flex-col gap-3">
                {jobs.map((j) => (
                  <PrintJobRow
                    key={j.id}
                    job={j}
                    expiresInLabel={formatExpiresIn(j.expiresAt, now)}
                    onCancel={(job) => setCancelTarget(job)}
                    busy={busy}
                  />
                ))}
              </div>
              <Text variant="secondary">{t('print.queue.hint')}</Text>
            </section>
          )}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <PrintUploadCard
              onFiles={(files) => void handleFiles(files)}
              disabled={busy}
              hasJobs={jobs.length > 0}
            />
            <PrintSteps />
          </div>
          <PrintHelp />
        </div>
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(next) => {
          if (!next) setCancelTarget(null)
        }}
        title={t('print.row.confirmCancelTitle')}
        description={t('print.row.confirmCancel')}
        confirmLabel={t('print.row.confirmCancelAction')}
        cancelLabel={t('common.back')}
        loading={busy}
        onConfirm={() => void confirmCancel()}
      />
    </div>
  )
}
